// Register summary (RFIs and notices): headline tiles, Responsibility (who has to act, and for how long) and open items by building.
// Every number is a filter. The Responsibility / Buildings switches and "Hide summary" are remembered per person, per browser.
import { useMemo, useState } from 'react';
import { isClosed } from '../lib/registers';
import { orgShort, type Org } from './item/shared';

export type SumRow = {
  id: string;
  number: string | null;
  status: string;
  building_ids: string[];
  respond_by: string | null;
  submitted_on: string | null;
  onus_org_id: string | null;
  onus_since: string | null;
  ss_created_at: string | null;
  created_at: string;
};

export type SumFilter = { f: string; onus: string; age: string; b: string };
type Group = 'consultant' | 'contractor' | 'swi' | 'other' | 'none';

const DAY = 86_400_000;
export const todayIso = () => new Date().toISOString().slice(0, 10);
/** date an item was raised: submitted, else first entered (Smartsheet or PM C3) */
export const raisedOn = (r: Pick<SumRow, 'submitted_on' | 'ss_created_at' | 'created_at'>) => r.submitted_on ?? (r.ss_created_at ?? r.created_at ?? '').slice(0, 10);
const daysFrom = (v: string | null | undefined) => {
  if (!v) return null;
  const t = new Date(v.length <= 10 ? v + 'T00:00:00' : v).getTime();
  return Number.isNaN(t) ? null : Math.max(0, Math.floor((Date.now() - t) / DAY));
};
/** days the item has been with whoever holds it now (since the onus last changed, else since it was raised) */
export const daysWithHolder = (r: SumRow) => daysFrom(r.onus_since) ?? daysFrom(raisedOn(r)) ?? 0;
export const isOverdue = (r: Pick<SumRow, 'status' | 'respond_by'>, t = todayIso()) => Boolean(r.respond_by && r.respond_by < t && !isClosed(r.status));
export const isSwi = (r: Pick<SumRow, 'status' | 'onus_org_id'>, swiId: string | undefined) => Boolean(swiId && r.onus_org_id === swiId && !isClosed(r.status));

export const AGE_BUCKETS = [
  { key: '0-7', label: '0–7', min: 0, max: 7, dot: '#EAD6C2' },
  { key: '8-14', label: '8–14', min: 8, max: 14, dot: '#E39A62' },
  { key: '15-30', label: '15–30', min: 15, max: 30, dot: '#C55A11' },
  { key: '30+', label: '30+', min: 31, max: Infinity, dot: '#7A2410' },
];
export const inAge = (r: SumRow, key: string) => {
  const b = AGE_BUCKETS.find((x) => x.key === key);
  if (!b) return true;
  const d = daysWithHolder(r);
  return d >= b.min && d <= b.max;
};

const groupOf = (o: Org | undefined, swiId: string | undefined): Group => {
  if (!o) return 'none';
  if (o.id === swiId || o.kind === 'internal') return 'swi';
  if (o.kind === 'architect' || o.kind === 'engineer' || o.kind === 'consultant') return 'consultant';
  if (o.kind === 'contractor' || o.kind === 'supplier') return 'contractor';
  return 'other';
};
const SHADES: Record<Group, { bg: string; fg: string }[]> = {
  consultant: [{ bg: '#1E5A8A', fg: '#fff' }, { bg: '#4F86B3', fg: '#fff' }, { bg: '#8DB3D1', fg: '#1A1A1A' }, { bg: '#2E4F6B', fg: '#fff' }, { bg: '#B9D0E3', fg: '#1A1A1A' }],
  contractor: [{ bg: '#A3470D', fg: '#fff' }, { bg: '#E39A62', fg: '#1A1A1A' }, { bg: '#7A350A', fg: '#fff' }, { bg: '#F2C29C', fg: '#1A1A1A' }],
  swi: [{ bg: '#1A1A1A', fg: '#fff' }],
  other: [{ bg: '#6B7F8C', fg: '#fff' }, { bg: '#A5B3BC', fg: '#1A1A1A' }],
  none: [{ bg: '#CFC4B6', fg: '#1A1A1A' }],
};
const GROUP_ORDER: Group[] = ['consultant', 'contractor', 'swi', 'other', 'none'];
const GROUP_NAME: Record<Group, string> = { consultant: 'Consultants', contractor: 'Contractor', swi: 'SWI', other: 'Others', none: 'not set' };
const BAR: Record<Group, string> = { consultant: '#1E5A8A', contractor: '#A3470D', swi: '#1A1A1A', other: '#6B7F8C', none: '#CFC4B6' };

// ---------- remembered per person, per browser ----------
type Prefs = { resp: boolean; bldg: boolean; hidden: boolean };
const prefKey = (uid: string | undefined, module: string) => `pmc3.summary.${uid ?? 'anon'}.${module}`;
function readPrefs(key: string): Prefs {
  try {
    const v = JSON.parse(window.localStorage.getItem(key) ?? '{}') ?? {};
    return { resp: v.resp !== false, bldg: v.bldg !== false, hidden: v.hidden === true };
  } catch {
    return { resp: true, bldg: true, hidden: false };
  }
}
export function useSummaryPrefs(uid: string | undefined, module: string) {
  const key = prefKey(uid, module);
  const [state, setState] = useState<{ key: string; p: Prefs }>(() => ({ key, p: readPrefs(key) }));
  const p = state.key === key ? state.p : readPrefs(key);
  const set = (next: Partial<Prefs>) => {
    const v = { ...p, ...next };
    try {
      window.localStorage.setItem(key, JSON.stringify(v));
    } catch {
      /* private window etc. — still works for this visit */
    }
    setState({ key, p: v });
  };
  return [p, set] as const;
}

export function RegisterSummary({
  rows,
  orgs,
  buildings,
  swiId,
  filter,
  setFilter,
  prefs,
  setPrefs,
  openItem,
}: {
  rows: SumRow[];
  orgs: Org[];
  buildings: { id: string; name: string }[];
  swiId: string | undefined;
  filter: SumFilter;
  setFilter: (k: keyof SumFilter, v: string) => void;
  prefs: Prefs;
  setPrefs: (p: Partial<Prefs>) => void;
  openItem: (id: string) => void;
}) {
  const s = useMemo(() => {
    const t = todayIso();
    const orgById = new Map(orgs.map((o) => [o.id, o]));
    const open = rows.filter((r) => !isClosed(r.status));
    const overdue = open.filter((r) => isOverdue(r, t)).length;
    const swi = open.filter((r) => isSwi(r, swiId));
    const swiWaiting = swi.filter((r) => daysWithHolder(r) > 7).length;
    const since30 = new Date(Date.now() - 30 * DAY).toISOString().slice(0, 10);
    const recent = rows.filter((r) => raisedOn(r) >= since30);
    const recentClosed = recent.filter((r) => isClosed(r.status)).length;
    const ages = open.map((r) => daysFrom(raisedOn(r)) ?? 0);
    const avgAge = ages.length ? ages.reduce((a, b) => a + b, 0) / ages.length : 0;
    let oldest: SumRow | null = null;
    let oldestDays = -1;
    open.forEach((r, i) => {
      if (ages[i] > oldestDays) {
        oldestDays = ages[i];
        oldest = r;
      }
    });

    // responsibility: one block per organisation, grouped consultants → contractor → SWI → others → not set
    const byOrg = new Map<string, number>();
    for (const r of open) byOrg.set(r.onus_org_id ?? 'none', (byOrg.get(r.onus_org_id ?? 'none') ?? 0) + 1);
    const groups: Record<Group, number> = { consultant: 0, contractor: 0, swi: 0, other: 0, none: 0 };
    const segs = [...byOrg.entries()]
      .map(([id, n]) => {
        const o = id === 'none' ? undefined : orgById.get(id);
        const g = id === 'none' ? 'none' : groupOf(o, swiId);
        groups[g] += n;
        return { id, n, g, name: id === 'none' ? 'Not set' : o ? orgShort(o) : 'Unknown', full: id === 'none' ? 'No responsibility set' : o?.name ?? 'Unknown organisation' };
      })
      .sort((a, b) => GROUP_ORDER.indexOf(a.g) - GROUP_ORDER.indexOf(b.g) || b.n - a.n);
    const used: Record<Group, number> = { consultant: 0, contractor: 0, swi: 0, other: 0, none: 0 };
    const blocks = segs.map((x) => {
      const shade = SHADES[x.g][used[x.g]++ % SHADES[x.g].length];
      return { ...x, ...shade };
    });
    const ageCounts = AGE_BUCKETS.map((b) => ({ ...b, n: open.filter((r) => inAge(r, b.key)).length }));

    // by building: open items, overdue, and who holds them
    const bmap = new Map<string, { id: string; name: string; open: number; late: number; g: Record<Group, number> }>();
    const bName = new Map(buildings.map((b) => [b.id, b.name]));
    for (const r of open) {
      const ids = r.building_ids.length ? r.building_ids : ['none'];
      for (const id of ids) {
        if (!bmap.has(id)) bmap.set(id, { id, name: id === 'none' ? 'No building' : bName.get(id) ?? 'Unknown', open: 0, late: 0, g: { consultant: 0, contractor: 0, swi: 0, other: 0, none: 0 } });
        const e = bmap.get(id)!;
        e.open++;
        if (isOverdue(r, t)) e.late++;
        e.g[r.onus_org_id ? groupOf(orgById.get(r.onus_org_id), swiId) : 'none']++;
      }
    }
    const bl = [...bmap.values()].sort((a, b) => (a.id === 'none' ? 1 : b.id === 'none' ? -1 : b.open - a.open || a.name.localeCompare(b.name)));
    return { open: open.length, total: rows.length, overdue, swi: swi.length, swiWaiting, recent: recent.length, recentClosed, avgAge, oldest: oldest as SumRow | null, oldestDays, blocks, groups, ageCounts, bl };
  }, [rows, orgs, buildings, swiId]);

  const toggle = (k: keyof SumFilter, v: string) => setFilter(k, filter[k] === v ? '' : v);
  const short = (n: string | null) => (n ? '_' + (/(\d+)\s*$/.exec(n)?.[1] ?? n) : '');
  const groupLine = GROUP_ORDER.filter((g) => s.groups[g]).map((g) => `${GROUP_NAME[g]} ${s.groups[g]}`).join(' · ');

  if (prefs.hidden) return null;
  return (
    <section className="rsum" aria-label="Summary">
      <div className="rsum-kpis">
        <button type="button" className={`rk ${filter.f === 'open' ? 'on' : ''}`} aria-pressed={filter.f === 'open'} onClick={() => toggle('f', 'open')}>
          <span className="lbl">Open</span>
          <b>{s.open}</b>
          <span className="sub">of {s.total}</span>
        </button>
        <button type="button" className={`rk late ${s.overdue ? 'attn' : ''} ${filter.f === 'overdue' ? 'on' : ''}`} aria-pressed={filter.f === 'overdue'} onClick={() => toggle('f', 'overdue')}>
          <span className="lbl">{s.overdue ? <span className="pdot" aria-hidden="true" /> : null}Overdue</span>
          <b>{s.overdue}</b>
          <span className="sub">past respond-by</span>
        </button>
        <button type="button" className={`rk swi ${s.swi ? 'attn' : ''} ${filter.f === 'swi' ? 'on' : ''}`} aria-pressed={filter.f === 'swi'} onClick={() => toggle('f', 'swi')}>
          <span className="lbl">{s.swi ? <span className="pdot o" aria-hidden="true" /> : null}SWI to act</span>
          <b>{s.swi}</b>
          <span className="sub">{s.swiWaiting ? `${s.swiWaiting} waiting over 7 d` : 'none waiting over 7 d'}</span>
        </button>
        <button type="button" className={`rk ${filter.f === 'recent' ? 'on' : ''}`} aria-pressed={filter.f === 'recent'} onClick={() => toggle('f', 'recent')}>
          <span className="lbl">Raised · 30 d</span>
          <b>{s.recent}</b>
          <span className="sub">{s.recentClosed} already closed</span>
        </button>
        <div className="rk static">
          <span className="lbl">Avg age open</span>
          <b>
            {Math.round(s.avgAge)}
            <small> d</small>
          </b>
          <span className="sub">
            {s.oldest ? (
              <>
                oldest {s.oldestDays} d ·{' '}
                <a href="#" onClick={(e) => (e.preventDefault(), openItem(s.oldest!.id))}>
                  {short(s.oldest.number)}
                </a>
              </>
            ) : (
              'nothing open'
            )}
          </span>
        </div>
      </div>

      <div className="rsum-show">
        <span>Show</span>
        <button type="button" className={`tg ${prefs.resp ? 'on' : ''}`} aria-pressed={prefs.resp} onClick={() => setPrefs({ resp: !prefs.resp })}>
          <span className="knob" aria-hidden="true" />
          Responsibility
        </button>
        <button type="button" className={`tg ${prefs.bldg ? 'on' : ''}`} aria-pressed={prefs.bldg} onClick={() => setPrefs({ bldg: !prefs.bldg })}>
          <span className="knob" aria-hidden="true" />
          Buildings
        </button>
      </div>

      {prefs.resp ? (
        <div className="rsum-resp">
          <div className="rr-bar-wrap">
            <div className="rr-head">
              <span className="lbl">Responsibility</span>
              <span className="muted small">{s.open ? groupLine : 'nothing open'}</span>
            </div>
            {s.open ? (
              <div className="rr-bar" role="group" aria-label="Open items by who has to act">
                {s.blocks.map((b) => {
                  const pct = b.n / s.open;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      className={`rr-seg ${filter.onus === b.id ? 'on' : ''}`}
                      style={{ flex: b.n, background: b.bg, color: b.fg }}
                      aria-pressed={filter.onus === b.id}
                      aria-label={`${b.full} ${b.n}`}
                      title={`${b.full} — ${b.n} open`}
                      onClick={() => toggle('onus', b.id)}
                    >
                      {pct >= 0.07 ? `${b.name} ${b.n}` : pct >= 0.025 ? b.n : ''}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
          <div className="rr-age">
            <span className="lbl">Days with current holder</span>
            <div className="rr-chips">
              {s.ageCounts.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  className={`ag ${a.key === '30+' && a.n ? 'attn late' : ''} ${filter.age === a.key ? 'on' : ''}`}
                  aria-pressed={filter.age === a.key}
                  onClick={() => toggle('age', a.key)}
                >
                  {a.key === '30+' && a.n ? <span className="pdot" aria-hidden="true" /> : <span className="adot" style={{ background: a.dot }} aria-hidden="true" />}
                  {a.label} <b>{a.n}</b>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {prefs.bldg ? (
        <div className="rsum-bldg">
          <div className="rr-head">
            <span className="lbl">By building · open</span>
            <span className="rr-legend">
              <span><i style={{ background: BAR.consultant }} />Consultants</span>
              <span><i style={{ background: BAR.contractor }} />Contractor</span>
              <span><i style={{ background: BAR.swi }} />SWI</span>
            </span>
          </div>
          {s.bl.length ? (
            <div className="rb-grid">
              {s.bl.map((b) => {
                const on = filter.b === b.id;
                return (
                  <button key={b.id} type="button" className={`rb ${on ? 'on' : ''} ${b.late ? 'has-late' : ''}`} aria-pressed={on} onClick={() => toggle('b', b.id)}>
                    <span className="rb-name">{b.name}</span>
                    <span className="rb-n">
                      <b>{b.open}</b>
                      {b.late ? <span className="pdot" aria-hidden="true" /> : null}
                      <span className={b.late ? 'rb-late' : 'muted'}>{b.late ? `${b.late} overdue` : '—'}</span>
                    </span>
                    <span className="rb-bar" aria-hidden="true">
                      {GROUP_ORDER.map((g) => (b.g[g] ? <span key={g} style={{ flex: b.g[g], background: BAR[g] }} /> : null))}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="muted small" style={{ margin: 0 }}>Nothing open.</p>
          )}
        </div>
      ) : null}
    </section>
  );
}
